import React, { useState } from 'react';
import { Modal, Button, Typography, Alert, Progress, List, Space, Tag, Divider, Table, Upload, message, Collapse } from 'antd';
import { UploadOutlined, DownloadOutlined, CheckCircleTwoTone, CloseCircleTwoTone, WarningTwoTone, InfoCircleOutlined } from '@ant-design/icons';
import { useMutation } from 'react-query';
import api from '../config/api';
import { formatShortCompanyId } from '../utils/companyDisplay';

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Static template URL (e.g. /templates/business_template.csv). Used when templateApiUrl is not set. */
  templateUrl: string;
  /** API endpoint that returns template file (e.g. /api/businesses/template). When set, download uses auth and returns Excel with all fields. */
  templateApiUrl?: string;
  uploadEndpoint: string;
  onSuccess?: () => void;
  entityType: 'businesses' | 'users';
}

interface UploadResult {
  success?: number;
  skipped: number;
  duplicates?: number;
  errors: number;
  total: number;
  reportUrl?: string;
  /** Bulk-update response: number of businesses updated */
  updated?: number;
  summary?: {
    uploaded?: Array<any>;
    updated?: Array<any>;
    skipped: Array<any>;
    duplicates?: Array<any>;
    errors: Array<any>;
  };
}

interface ValidationResult {
  valid: boolean;
  totalRows: number;
  errors: Array<{
    row: number;
    field: string;
    error: string;
  }>;
  warnings: Array<{
    row: number;
    field: string;
    warning: string;
  }>;
  duplicatesInCSV: Array<{
    row: number;
    previousRow: number;
    field: string;
    value: string;
    error: string;
  }>;
  duplicatesInDB: Array<{
    row: number;
    field: string;
    value: string;
    existingBusiness?: any;
    error: string;
  }>;
}

const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({
  open,
  onClose,
  title,
  templateUrl,
  templateApiUrl,
  uploadEndpoint,
  onSuccess,
  entityType
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [uploadMode, setUploadMode] = useState<'create' | 'update' | null>(null);
  const [updatePreview, setUpdatePreview] = useState<{
    totalRows: number;
    preview: Array<{ row: number; business_id: string; business_name: string; mid: string; updates: Record<string, { from: any; to: any }> }>;
    notFound: Array<{ row: number; id: string | null; mid: string | null; business_name: string }>;
    errors: Array<{ row: number; error: string }>;
    valid: boolean;
  } | null>(null);

  const isBusinessUpdateMode = entityType === 'businesses' && uploadMode === 'update';

  // Validation mutation
  const validateMutation = useMutation(
    async (formData: FormData) => {
      let validateEndpoint: string;
      if (entityType === 'businesses' && uploadMode === 'update') {
        validateEndpoint = '/api/businesses/validate-bulk-update';
      } else {
        validateEndpoint = entityType === 'businesses' ? '/api/businesses/validate-bulk' : '/api/users/validate-bulk';
      }
      const response = await api.post(validateEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        setIsValidating(false);
        if (isBusinessUpdateMode) {
          setUpdatePreview(data);
          setValidationResult(null);
          if (data.valid && data.preview?.length > 0) {
            message.success(`Preview: ${data.preview.length} businesses will be updated`);
            setShowConfirmation(true);
          } else if (data.errors?.length > 0 || data.notFound?.length > 0) {
            const errCount = (data.errors?.length || 0) + (data.notFound?.length || 0);
            message.warning(`${data.preview?.length || 0} to update, ${errCount} rows with issues`);
            setShowConfirmation(data.preview?.length > 0);
          } else if (!data.preview?.length) {
            message.info('No rows with changes to apply');
          }
        } else {
          setUpdatePreview(null);
          setValidationResult(data as ValidationResult);
          const vr = data as ValidationResult;
          if (vr.valid) {
            if (vr.warnings?.length > 0) {
              message.warning(`Validation passed with ${vr.warnings.length} warnings`);
            } else {
              message.success(`Validation passed! ${vr.totalRows} rows ready to upload`);
            }
            setShowConfirmation(true);
          } else {
            const totalErrors = (vr.errors?.length || 0) + (vr.duplicatesInCSV?.length || 0) + (vr.duplicatesInDB?.length || 0);
            message.error(`Validation failed with ${totalErrors} errors`);
          }
        }
      },
      onError: (error: any) => {
        setIsValidating(false);
        message.error(error.response?.data?.error || 'Validation failed');
      }
    }
  );

  const effectiveUploadEndpoint = entityType === 'businesses' && uploadMode === 'update'
    ? '/api/businesses/bulk-update'
    : uploadEndpoint;

  // Upload mutation
  const uploadMutation = useMutation(
    async (formData: FormData) => {
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      try {
        const response = await api.post(effectiveUploadEndpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        clearInterval(interval);
        setUploadProgress(100);
        return response.data;
      } catch (error) {
        clearInterval(interval);
        throw error;
      }
    },
    {
      onSuccess: (data: any) => {
        setUploadResult(data);
        setShowConfirmation(false);
        setUpdatePreview(null);

        if (entityType === 'businesses' && uploadMode === 'update') {
          const successMsg = [];
          if (data.updated > 0) successMsg.push(`${data.updated} updated`);
          if (data.skipped > 0) successMsg.push(`${data.skipped} skipped`);
          if (data.errors > 0) successMsg.push(`${data.errors} errors`);
          if (data.updated > 0) {
            message.success(`Bulk update complete: ${successMsg.join(', ')}`);
          } else {
            message.warning(`Bulk update complete: ${successMsg.join(', ')}`);
          }
        } else {
          const successMsg = [];
          if (data.success > 0) successMsg.push(`${data.success} uploaded`);
          if (data.skipped > 0) successMsg.push(`${data.skipped} skipped (already in database)`);
          if (data.duplicates > 0) successMsg.push(`${data.duplicates} duplicates in CSV`);
          if (data.errors > 0) successMsg.push(`${data.errors} errors`);
          if (data.success > 0) {
            message.success(`Upload complete: ${successMsg.join(', ')}`);
          } else {
            message.warning(`Upload complete: ${successMsg.join(', ')}`);
          }
        }
        onSuccess?.();
      },
      onError: (error: any) => {
        setUploadProgress(0);
        message.error(error.response?.data?.error || 'Upload failed');
      }
    }
  );

  const handleFileChange = (selectedFile: File) => {
    setFile(selectedFile);
    setUploadResult(null);
    setValidationResult(null);
    setShowConfirmation(false);
    setUploadProgress(0);
    
    // Automatically validate the file
    setIsValidating(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    validateMutation.mutate(formData);
  };

  const handleConfirmUpload = () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    uploadMutation.mutate(formData);
  };

  const handleDownloadTemplate = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (templateApiUrl) {
      setTemplateDownloading(true);
      try {
        const response = await api.get(templateApiUrl, { responseType: 'blob' });
        const blob = response.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = entityType === 'businesses' ? 'business_bulk_upload_template.xlsx' : `${entityType}_template.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('Template downloaded');
      } catch (e: any) {
        message.error(e?.response?.data?.error || 'Failed to download template');
      } finally {
        setTemplateDownloading(false);
      }
      return;
    }
    
    const link = document.createElement('a');
    link.href = templateUrl;
    link.download = `${entityType}_template.csv`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    setValidationResult(null);
    setShowConfirmation(false);
    setIsValidating(false);
    setUploadProgress(0);
    setUploadMode(null);
    setUpdatePreview(null);
    onClose();
  };

  const totalErrors = validationResult 
    ? validationResult.errors.length + validationResult.duplicatesInCSV.length + validationResult.duplicatesInDB.length 
    : 0;

  const formatUpdateValue = (field: string, val: unknown) => {
    if (val == null || val === '') return '—';
    if (field === 'company_id') return formatShortCompanyId(String(val)) || '—';
    return String(val);
  };

  const showModeChoice = open && entityType === 'businesses' && uploadMode === null;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleClose}
      width={900}
      footer={
        showModeChoice
          ? null
          : [
              <Button key="cancel" onClick={handleClose} disabled={uploadMutation.isLoading}>
                {uploadResult ? 'Close' : 'Cancel'}
              </Button>,
              showConfirmation && (
                <Button 
                  key="confirm" 
                  type="primary" 
                  onClick={handleConfirmUpload} 
                  disabled={uploadMutation.isLoading}
                  loading={uploadMutation.isLoading}
                >
                  {uploadMutation.isLoading 
                    ? (isBusinessUpdateMode ? 'Updating...' : 'Uploading...') 
                    : (isBusinessUpdateMode ? 'Confirm & Update' : 'Confirm & Upload')}
                </Button>
              )
            ]
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {showModeChoice && (
          <>
            <Typography.Paragraph strong>What do you want to do?</Typography.Paragraph>
            <Space size="middle">
              <Button
                type="primary"
                size="large"
                onClick={() => setUploadMode('create')}
              >
                Add new businesses
              </Button>
              <Button
                size="large"
                onClick={() => setUploadMode('update')}
              >
                Update existing businesses
              </Button>
            </Space>
            <Typography.Text type="secondary">
              Add new: create businesses from the file (rows with existing MID/Store ID will be skipped). Update existing: match rows by ID or MID and update fields like Store ID or Company Name. Company IDs are 8 characters — see Users → Companies or the template Companies sheet.
            </Typography.Text>
          </>
        )}

        {!showModeChoice && (
          <>
        <Alert type="info" showIcon message={
          isBusinessUpdateMode 
            ? <span>Use an export or template with <strong>ID</strong> or <strong>MID</strong> to match existing businesses. Fill in <strong>Company Name</strong> or the 8-character <strong>Company ID</strong> from Users → Companies. Excel (.xlsx) and CSV supported.</span>
            : <span>Download the template file, fill it out with your data, and upload it here. Use <strong>Company Name</strong> or the 8-character <strong>Company ID</strong> (from Users → Companies or the Companies sheet). Excel (.xlsx) and CSV files are supported. The file will be validated before upload.</span>
        } />

        <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} loading={templateDownloading}>Download Template</Button>

        <Divider />

        <div>
          <Typography.Title level={5} style={{ marginTop: 0 }}>Upload Excel File</Typography.Title>
          <Upload
            beforeUpload={(f) => { 
              handleFileChange(f as File);
              return false; 
            }}
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            showUploadList={file ? { showRemoveIcon: true } : false}
            onRemove={() => { 
              setFile(null); 
              setValidationResult(null); 
              setShowConfirmation(false);
              setUploadProgress(0);
            }}
            disabled={isValidating || uploadMutation.isLoading}
          >
            <Button 
              icon={<UploadOutlined />} 
              disabled={isValidating || uploadMutation.isLoading}
            >
              Choose Excel or CSV File
            </Button>
          </Upload>
          {file && (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Selected: {file.name}
            </Typography.Text>
          )}
        </div>

        {isValidating && (
          <div>
            <Typography.Text><InfoCircleOutlined /> Validating file...</Typography.Text>
            <Progress percent={50} status="active" showInfo={false} style={{ marginTop: 8 }} />
          </div>
        )}

        {updatePreview && (
          <div>
            <Typography.Title level={5}>Update preview</Typography.Title>
            <Space size="small" style={{ marginBottom: 12 }} wrap>
              <Tag color="blue">{updatePreview.totalRows} total rows</Tag>
              <Tag color="green">{updatePreview.preview?.length || 0} businesses to update</Tag>
              {(updatePreview.notFound?.length || 0) > 0 && (
                <Tag color="orange">{updatePreview.notFound?.length} not found (missing ID/MID)</Tag>
              )}
              {(updatePreview.errors?.length || 0) > 0 && (
                <Tag color="red">{updatePreview.errors?.length} errors</Tag>
              )}
            </Space>
            {updatePreview.preview && updatePreview.preview.length > 0 && (
              <Table
                size="small"
                pagination={{ pageSize: 5 }}
                dataSource={updatePreview.preview}
                rowKey={(r) => `${r.row}-${r.business_id}`}
                columns={[
                  { title: 'Row', dataIndex: 'row', width: 60 },
                  { title: 'Business', dataIndex: 'business_name', ellipsis: true },
                  { title: 'MID', dataIndex: 'mid', width: 100 },
                  {
                    title: 'Changes',
                    key: 'updates',
                    render: (_: any, record: any) => (
                      <div>
                        {record.updates && Object.entries(record.updates).map(([field, change]: [string, any]) => (
                          <div key={field} style={{ fontSize: 12 }}>
                            <Typography.Text type="secondary">{field === 'company_id' ? 'company' : field}:</Typography.Text>{' '}
                            {formatUpdateValue(field, change.from)} → {formatUpdateValue(field, change.to)}
                          </div>
                        ))}
                      </div>
                    ),
                  },
                ]}
              />
            )}
            {(updatePreview.notFound?.length || 0) > 0 && (
              <Collapse style={{ marginTop: 12 }}>
                <Collapse.Panel header={`Not found (${updatePreview.notFound?.length})`} key="notFound">
                  <List
                    size="small"
                    dataSource={updatePreview.notFound?.slice(0, 20)}
                    renderItem={(item: any) => (
                      <List.Item>Row {item.row}: {item.business_name || item.mid || item.id || '—'}</List.Item>
                    )}
                  />
                  {(updatePreview.notFound?.length || 0) > 20 && (
                    <Typography.Text type="secondary">... and {updatePreview.notFound!.length - 20} more</Typography.Text>
                  )}
                </Collapse.Panel>
              </Collapse>
            )}
            {(updatePreview.errors?.length || 0) > 0 && (
              <Collapse style={{ marginTop: 12 }}>
                <Collapse.Panel header={`Errors (${updatePreview.errors?.length})`} key="errors">
                  <List
                    size="small"
                    dataSource={updatePreview.errors}
                    renderItem={(item: any) => (
                      <List.Item><Typography.Text type="danger">Row {item.row}: {item.error}</Typography.Text></List.Item>
                    )}
                  />
                </Collapse.Panel>
              </Collapse>
            )}
          </div>
        )}

        {validationResult && (
          <div>
            <Typography.Title level={5}>Validation Results</Typography.Title>
            
            {/* Summary Cards */}
            <Space size="small" style={{ marginBottom: 16 }}>
              <Tag icon={<InfoCircleOutlined />} color="blue">{validationResult.totalRows} total rows</Tag>
              {totalErrors > 0 && (
                <Tag icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />} color="error">{totalErrors} errors</Tag>
              )}
              {validationResult.warnings.length > 0 && (
                <Tag icon={<WarningTwoTone twoToneColor="#faad14" />} color="warning">{validationResult.warnings.length} warnings</Tag>
              )}
              {validationResult.valid && totalErrors === 0 && (
                <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">Ready to upload</Tag>
              )}
            </Space>

            {/* Errors Section */}
            {totalErrors > 0 && (
              <Alert
                type="error"
                showIcon
                message={`${totalErrors} Errors Found - Upload Blocked`}
                description="Fix these errors in your CSV file and try again."
                style={{ marginBottom: 12 }}
              />
            )}

            <Collapse 
              bordered={false}
              style={{ background: '#f5f5f5' }}
              items={[
                // Field Validation Errors
                validationResult.errors.length > 0 && {
                  key: 'errors',
                  label: (
                    <span>
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                      Field Validation Errors ({validationResult.errors.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.errors}
                      renderItem={(err) => (
                        <List.Item>
                          <Typography.Text>
                            Row {err.row} - {err.field}: <Typography.Text type="danger">{err.error}</Typography.Text>
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
                // Duplicates in CSV
                validationResult.duplicatesInCSV.length > 0 && {
                  key: 'duplicates-csv',
                  label: (
                    <span>
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                      Duplicates Within CSV ({validationResult.duplicatesInCSV.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.duplicatesInCSV}
                      renderItem={(dup) => (
                        <List.Item>
                          <Typography.Text>
                            Row {dup.row} - {dup.field}: <Typography.Text type="danger">"{dup.value}"</Typography.Text> already exists at row {dup.previousRow}
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
                // Duplicates in Database
                validationResult.duplicatesInDB.length > 0 && {
                  key: 'duplicates-db',
                  label: (
                    <span>
                      <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                      Already Exists in Database ({validationResult.duplicatesInDB.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.duplicatesInDB}
                      renderItem={(dup) => (
                        <List.Item>
                          <Typography.Text>
                            Row {dup.row} - {dup.field}: <Typography.Text type="danger">"{dup.value}"</Typography.Text>
                            {dup.existingBusiness && ` - ${dup.existingBusiness.business_name}`}
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
                // Warnings
                validationResult.warnings.length > 0 && {
                  key: 'warnings',
                  label: (
                    <span>
                      <WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 8 }} />
                      Warnings ({validationResult.warnings.length})
                    </span>
                  ),
                  children: (
                    <List
                      size="small"
                      dataSource={validationResult.warnings}
                      renderItem={(warn) => (
                        <List.Item>
                          <Typography.Text>
                            Row {warn.row} - {warn.field}: <Typography.Text type="warning">{warn.warning}</Typography.Text>
                          </Typography.Text>
                        </List.Item>
                      )}
                    />
                  ),
                },
              ].filter(Boolean) as any[]}
            />

            {validationResult.valid && totalErrors === 0 && (
              <Alert
                type="success"
                showIcon
                message="Validation Passed!"
                description={`${validationResult.totalRows} rows are ready to be uploaded. Click "Confirm & Upload" to proceed.`}
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        )}

        {uploadMutation.isLoading && (
          <div>
            <Typography.Text><InfoCircleOutlined /> Uploading and processing...</Typography.Text>
            <Progress percent={uploadProgress} status="active" style={{ marginTop: 8 }} />
          </div>
        )}

        {uploadResult && (
          <div>
            <Divider />
            <Typography.Title level={5}>{isBusinessUpdateMode ? 'Bulk Update Complete' : 'Upload Complete'}</Typography.Title>
            
            {/* Summary Tags - support both create and update response shapes */}
            <Space size="small" style={{ marginBottom: 16 }} wrap>
              {isBusinessUpdateMode && (uploadResult as unknown as { updated?: number }).updated !== undefined ? (
                <>
                  <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
                    {(uploadResult as unknown as { updated?: number }).updated} updated
                  </Tag>
                  {uploadResult.skipped > 0 && (
                    <Tag icon={<InfoCircleOutlined />} color="blue">{uploadResult.skipped} skipped</Tag>
                  )}
                  {uploadResult.errors > 0 && (
                    <Tag icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />} color="error">{uploadResult.errors} errors</Tag>
                  )}
                  <Tag color="default">{uploadResult.total} total rows</Tag>
                </>
              ) : (
                <>
                  <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success">
                    {uploadResult.success ?? 0} uploaded
                  </Tag>
                  {uploadResult.skipped > 0 && (
                    <Tag icon={<InfoCircleOutlined />} color="blue">
                      {uploadResult.skipped} skipped (already in DB)
                    </Tag>
                  )}
                  {(uploadResult.duplicates ?? 0) > 0 && (
                    <Tag icon={<WarningTwoTone twoToneColor="#faad14" />} color="warning">
                      {uploadResult.duplicates} duplicates in CSV
                    </Tag>
                  )}
                  {uploadResult.errors > 0 && (
                    <Tag icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />} color="error">
                      {uploadResult.errors} errors
                    </Tag>
                  )}
                  <Tag color="default">
                    {uploadResult.total} total rows processed
                  </Tag>
                </>
              )}
            </Space>

            {/* Download Report - only for create upload */}
            {!isBusinessUpdateMode && uploadResult.reportUrl && (
            <Alert
              type="info"
              showIcon
              message="Detailed Report Available"
              description={
                <Space direction="vertical" size="small">
                  <Typography.Text>
                    A detailed Excel report has been generated showing which businesses were uploaded, skipped, or had errors.
                  </Typography.Text>
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      window.open(uploadResult.reportUrl, '_blank');
                    }}
                  >
                    Download Detailed Report (Excel)
                  </Button>
                </Space>
              }
              style={{ marginBottom: 16 }}
            />
            )}

            {/* Quick Summary in Collapsible */}
            {uploadResult.summary && (
              <Collapse 
                bordered={false}
                style={{ background: '#f5f5f5' }}
                items={[
                  ((uploadResult.summary.uploaded || uploadResult.summary.updated)?.length ?? 0) > 0 && {
                    key: 'uploaded',
                    label: (
                      <span>
                        <CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 8 }} />
                        {isBusinessUpdateMode ? 'Updated' : 'Uploaded'} ({(uploadResult.summary.uploaded || uploadResult.summary.updated || []).length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={(uploadResult.summary.uploaded || uploadResult.summary.updated || []).slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text>
                              Row {item.row}: {item.business_name} - {item.mid || ''}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={(uploadResult.summary.uploaded || uploadResult.summary.updated || []).length > 10 && (
                          <Typography.Text type="secondary">
                            ... and {(uploadResult.summary.uploaded || uploadResult.summary.updated || []).length - 10} more.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                  (uploadResult.summary.skipped?.length ?? 0) > 0 && {
                    key: 'skipped',
                    label: (
                      <span>
                        <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                        Skipped ({(uploadResult.summary.skipped || []).length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={(uploadResult.summary.skipped || []).slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text>
                              Row {item.row}: {item.business_name} - {item.mid} - {item.reason}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={(uploadResult.summary.skipped || []).length > 10 && (
                          <Typography.Text type="secondary">
                            ... and {(uploadResult.summary.skipped || []).length - 10} more. Download the report for full details.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                  ((uploadResult.summary.duplicates?.length) ?? 0) > 0 && {
                    key: 'duplicates',
                    label: (
                      <span>
                        <WarningTwoTone twoToneColor="#faad14" style={{ marginRight: 8 }} />
                        Duplicates in CSV ({(uploadResult.summary.duplicates || []).length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={(uploadResult.summary.duplicates || []).slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text type="warning">
                              Row {item.row}: {item.business_name} - {item.mid} - {item.reason}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={(uploadResult.summary.duplicates || []).length > 10 && (
                          <Typography.Text type="secondary">
                            ... and {(uploadResult.summary.duplicates || []).length - 10} more. Download the report for full details.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                  (uploadResult.summary.errors?.length || 0) > 0 && {
                    key: 'errors',
                    label: (
                      <span>
                        <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ marginRight: 8 }} />
                        Errors ({(uploadResult.summary.errors || []).length})
                      </span>
                    ),
                    children: (
                      <List
                        size="small"
                        dataSource={(uploadResult.summary.errors || []).slice(0, 10)}
                        renderItem={(item: any) => (
                          <List.Item>
                            <Typography.Text type="danger">
                              Row {item.row}: {item.business_name} - {item.reason}
                            </Typography.Text>
                          </List.Item>
                        )}
                        footer={(uploadResult.summary.errors?.length || 0) > 10 && (
                          <Typography.Text type="secondary">
                            ... and {(uploadResult.summary.errors?.length || 0) - 10} more.
                          </Typography.Text>
                        )}
                      />
                    ),
                  },
                ].filter(Boolean) as any[]}
              />
            )}
          </div>
        )}
          </>
        )}
      </Space>
    </Modal>
  );
};

export default BulkUploadDialog;
